import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { toTitleCase, standardReplacements, existsFilter } from '@iotv/core-services';
import { Checkbox, InputAdornment, Stack, Typography } from '@mui/material';
import { isBoolean, isNonNaNNumber, isValidAttributeKeyDefinitionOfType, verify, } from '@iotv/iotv-v3-types';
import { AutosizedTextField } from '../../../../../../../components/common/AutosizedTextField';
import { useContext, useState } from 'react';
import { DeviceControlContext } from '../../DeviceControlContext';
import { getValidRangeText } from './subFns';
import { Key } from '@mui/icons-material';
import { standardOutlinedStackProps, standardStackLabelProps } from './cosmetics';
const debug = process.env.REACT_APP_DEBUG && false;
const FlatMapNumericControl = ({ pseudoDispatch, parentKey, subKey, include, maxWidth = '36ch', validRangeText, unitLabel, currentValue, reportedValue, }) => {
    const [nonNumVal, setNonNumVal] = useState(null);
    const handleNumChange = (e) => {
        const value = e.target.value;
        const parsedValue = parseFloat(value);
        if (!isNaN(parsedValue)) {
            setNonNumVal(null);
            pseudoDispatch(parsedValue);
        }
        else {
            setNonNumVal(value);
        }
    };
    return (_jsx(AutosizedTextField, { label: toTitleCase(standardReplacements[subKey] ?? subKey),
        disabled: !include,
        value: nonNumVal !== null ? nonNumVal : currentValue,
        onChange: handleNumChange,
        slotProps: {
            inputLabel: {
                shrink: true,
            },
            input: {
                type: 'number',
                sx: { width: maxWidth },
                startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx(Stack, { sx: { width: '10ch' }, children: _jsx(Typography, { children: validRangeText }) }) })),
                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsxs(Stack, { direction: 'row', alignItems: 'flex-end', gap: 1, children: [_jsx(Typography, { children: unitLabel ?? 'Units' }), _jsxs(Typography, { children: ["(", _jsx(Typography, { component: "span", sx: { fontStyle: 'italic' }, children: "Reported" }), ": ", isNonNaNNumber(reportedValue) ? reportedValue : 'N/A', ")"] })] }) }, `${subKey}-end-adornment`)),
            },
        } }, `${parentKey}.${subKey}`));
};
// type SolenoidControllerOptionsKeys = keyof typeof solenoidControllerOptionsTemplate
export const FlatMapControl = ({ state, dispatch, stateKey: key, label, unitLabel, maxWidth, reported }) => {
    const { valueSettings, payloadRequestObject, validationMap } = state;
    const { state: DeviceControlContextState, dispatch: DeviceControlDispatch } = useContext(DeviceControlContext);
    const { rangeLimits, resolvedDownlinkAttributes, correspondingUplinkAttributeMap } = DeviceControlContextState;
    const subRangeLimits = rangeLimits?.[key];
    const mapValidation = validationMap[key] ?? {};
    const correspondingUplinkKey = correspondingUplinkAttributeMap?.[key] ?? Key;
    const topLevelKey = key.split('.')[0];
    if (!valueSettings[topLevelKey]) {
        return _jsx(Typography, { children: `Can't find key ${topLevelKey} (full path: ${key})` });
    }
    else {
        const { include, value } = valueSettings[topLevelKey];
        if (typeof value === 'object') {
            const currentMap = value;
            const mapAttributeControls = Object.entries(value).map(([k, v]) => {
                const attributeValidation = subRangeLimits?.[k];
                const isVerifyField = isValidAttributeKeyDefinitionOfType(attributeValidation, 'number') &&
                    attributeValidation.range.max === verify &&
                    attributeValidation.range.min === verify;
                const validRangeText = getValidRangeText(attributeValidation);
                const rawReportedValue = reported?.[correspondingUplinkKey] ?? undefined;
                // left generic
                const handleAttChange = (k, v) => {
                    const clonedMap = { ...currentMap };
                    const bool = isBoolean(v) ? v : undefined;
                    const possibleNumber = isNonNaNNumber(v) ? v : parseFloat(String(v));
                    const number = isNonNaNNumber(possibleNumber) ? possibleNumber : undefined;
                    const set = bool ?? number ?? v;
                    clonedMap[k] = set;
                    dispatch({ type: 'UpdateMap', update: { key, v: clonedMap } });
                };
                return isVerifyField ? null : ( // do not render verify fields, they are handled by defaults
                _jsx(FlatMapNumericControl, { currentValue: v,
                    reportedValue: rawReportedValue ? rawReportedValue[k] : undefined,
                    pseudoDispatch: (newValue) => handleAttChange(k, newValue),
                    parentKey: key,
                    subKey: k,
                    include,
                    maxWidth,
                    validRangeText,
                    unitLabel }, `${key}.${k}`));
            });
            return (_jsx(Stack, { direction: 'row', alignItems: 'center', paddingBlock: 1, gap: 1, maxWidth, children: _jsxs(Stack, { ...standardOutlinedStackProps,
                    maxWidth: maxWidth ? maxWidth : '100%',
                    position: 'relative',
                    paddingBlock: 1.5, children: [_jsx(Typography, { ...standardStackLabelProps, children: `${label}` }), _jsxs(Stack, { direction: 'row', alignItems: 'flex-start', gap: 1, width: mapAttributeControls.filter(existsFilter).length === 0 ? '24ch' : undefined, children: [_jsx(Checkbox, { checked: include,
                                    sx: { padding: 0, mt: 1 },
                                    onClick: () => {
                                        dispatch({ type: 'TogglePresence', update: { key } });
                                    } }), _jsx(Stack, { direction: 'column', alignItems: 'flex-start', gap: 1, width: undefined, children: mapAttributeControls })] })] }) }));
        }
        else {
            return _jsx(Typography, { children: `Not number at ${key}: ${JSON.stringify(value)}` });
        }
    }
};
